const normalize = name => name.split(' ').join('').toLowerCase();

// Helper to convert column number to letter (1 -> A, 27 -> AA, etc.)
const columnToLetter = column => {
  let letter = '';
  while (column > 0) {
    const remainder = (column - 1) % 26;
    letter = String.fromCharCode(65 + remainder) + letter;
    column = Math.floor((column - 1) / 26);
  }
  return letter;
};

export const getSheet = (workbook, normalizedSheetName = '') => {
  const sheetNames = workbook.worksheets.map(ws => ws.name);
  const sheetName = sheetNames.find(name =>
    normalize(name) === normalizedSheetName
  ) || sheetNames[0];

  const worksheet = workbook.getWorksheet(sheetName);

  // Add xlsx compatibility property !ref for range
  if (worksheet && !worksheet['!ref']) {
    const lastCol = columnToLetter(worksheet.columnCount);
    const lastRow = worksheet.rowCount;
    worksheet['!ref'] = `A1:${lastCol}${lastRow}`;
  }

  return worksheet;
};

export const getNormalizedKeyName = (key = '') => key
  .split(' ')
  .filter(part => !part.toLowerCase().includes('(optional)'))
  .join('')
  .toLowerCase();

export const normalizeCellToNumber = cell => {
  if (typeof cell.v === 'number' && cell.t === 'n') {
    return cell;
  }
  if (!isNaN(cell.v)) {
    const { r, h, ...rest } = cell; // eslint-disable-line no-unused-vars
    return {
      ...rest,
      t: 'n',
      v: Number(cell.v),
    };
  }
  return cell;
};

export const normalizeCellToNullableNumber = cell => {
  if (typeof cell.v === 'number' && cell.t === 'n') {
    return cell;
  }
  if (!isNaN(cell.v)) {
    const { r, h, ...rest } = cell; // eslint-disable-line no-unused-vars
    return {
      ...rest,
      t: 'n',
      v: cell.v !== '' && cell.v !== null ? Number(cell.v): null,
    };
  }
  return cell;
};

export const normalizeCellToString = cell => ({
  ...cell,
  t: 's',
  v: cell.w,
});

export const flattenRichTextValue = cell => {
  const value = cell && cell.value;
  if (value && typeof value === 'object' && Array.isArray(value.richText)) {
    return (cell.text !== null && typeof cell.text !== 'undefined')
      ? cell.text
      : value.richText.map(part => (part && part.text) || '').join('');
  }
  return value;
};

export const normalizeSheet = normalizers => sheet => {
  const headers = [];
  const firstRow = sheet.getRow(1);

  // Get headers from first row
  for (let colNum = 1; colNum <= sheet.columnCount; colNum++) {
    const cell = firstRow.getCell(colNum);
    headers[colNum - 1] = cell.value ? getNormalizedKeyName(String(cell.value)) : null;
  }

  // Process data rows
  for (let rowNum = 2; rowNum <= sheet.rowCount; rowNum++) {
    const row = sheet.getRow(rowNum);

    for (let colNum = 1; colNum <= sheet.columnCount; colNum++) {
      const headerKey = headers[colNum - 1];
      const cell = row.getCell(colNum);
      const isSafeHeaderKey = headerKey
        && !['__proto__', 'constructor', 'prototype'].includes(headerKey);
      const hasOwnNormalizer = isSafeHeaderKey
        && Object.prototype.hasOwnProperty.call(normalizers, headerKey);
      const normalizer = hasOwnNormalizer ? normalizers[headerKey] : null;

      if (typeof normalizer === 'function' && cell.value !== null && typeof cell.value !== 'undefined') {
        const cellType = typeof cell.value === 'number' ? 'n' : 's';
        const normalizedCell = normalizer({
          v: cell.value,
          t: cellType,
          w: cell.text || String(cell.value),
        });
        cell.value = normalizedCell.v;
      }
    }
  }

  return sheet;
};

export const mapToJson = ({
  sheet,
  fieldMappings = {},
  options = { raw: true },
  fieldParsers = {},
} = {}) => {
  // Parse range if provided
  let startRow = 1;
  let endRow = sheet.rowCount;
  const startCol = 1;
  const endCol = sheet.columnCount;

  if (options.range) {
    const rangeMatch = options.range.match(/([A-Z]+)(\d+):([A-Z]+)(\d+)/);
    if (rangeMatch) {
      startRow = parseInt(rangeMatch[2], 10);
      endRow = parseInt(rangeMatch[4], 10);
    }
  }

  // Get headers from options or first row
  const headers = options.header || [];
  const headerRow = sheet.getRow(startRow);
  const columnHeaders = [];

  // Build column headers array
  for (let colNum = startCol; colNum <= endCol; colNum++) {
    const cell = headerRow.getCell(colNum);
    if (headers.length > 0 && headers[colNum - startCol]) {
      columnHeaders[colNum - startCol] = headers[colNum - startCol];
    } else {
      columnHeaders[colNum - startCol] = cell.value ? String(cell.value) : '';
    }
  }

  // Convert sheet to JSON array
  const sheetAsJson = [];
  const dataStartRow = options.header ? startRow : startRow + 1;

  for (let rowNum = dataStartRow; rowNum <= endRow; rowNum++) {
    const row = sheet.getRow(rowNum);
    const rowData = {};
    for (let colNum = startCol; colNum <= endCol; colNum++) {
      const headerKey = columnHeaders[colNum - startCol];
      if (headerKey) {
        const cell = row.getCell(colNum);
        const cellValue = options.raw ? flattenRichTextValue(cell) : (cell.text || String(cell.value || ''));
        rowData[headerKey] = (cellValue !== null && typeof cellValue !== 'undefined') ? cellValue : '';
      }
    }

    // Always add row to maintain row indices (needed for empty row detection)
    sheetAsJson.push(rowData);
  }

  // Create safe initial entity without prototype
  const initialEntity = Object.create(null);
  Object.values(fieldMappings).forEach(key => {
    if (typeof key === 'string' && !['__proto__', 'constructor', 'prototype'].includes(key)) {
      initialEntity[key] = '';
    }
  });

  const sheetAsJsonMapped = sheetAsJson.map(row => {
    const rowKeys = Object.keys(row);

    return rowKeys.reduce((result, currentKey) => {
      const normalizedKey = getNormalizedKeyName(currentKey);
      const mappedKey = fieldMappings[normalizedKey];

      // Only process safe keys
      if (mappedKey && typeof mappedKey === 'string'
          && !['__proto__', 'constructor', 'prototype'].includes(mappedKey)) {
        const fieldParser = fieldParsers && fieldParsers[normalizedKey];
        const fieldValue = fieldParser ? fieldParser(row[currentKey]) : row[currentKey];
        result[mappedKey] = fieldValue;
      }

      return result;
    }, Object.assign(Object.create(null), initialEntity));
  });

  return sheetAsJsonMapped;
};
